/**
 * PromoBanner Component
 *
 * Displays promotional banners at the top of the page.
 * Fetches active promos with display_type='banner'.
 * Shows every non-dismissed banner stacked (e.g. Thanksgiving + birthday).
 * Users can dismiss each banner (remembered per session).
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { HiX, HiSpeakerphone, HiTag, HiGift } from 'react-icons/hi';

interface Promo {
  id: string;
  title: string;
  description: string | null;
  discount_type: 'percentage' | 'fixed' | 'info' | null;
  discount_value: number | null;
  promo_code: string | null;
  free_item: string | null;
  display_type: string;
  is_daily_special: boolean;
  /** Site path such as /menu/thanksgiving-2026. Empty means the bar is text only. */
  link_path?: string | null;
}

function BannerRow({
  promo,
  onDismiss,
}: {
  promo: Promo;
  onDismiss: (id: string) => void;
}) {
  let discountText = '';
  if (promo.discount_type === 'percentage' && promo.discount_value) {
    discountText = `${promo.discount_value}% OFF`;
  } else if (promo.discount_type === 'fixed' && promo.discount_value) {
    discountText = `$${promo.discount_value} OFF`;
  }

  const message = (
    <>
      {promo.is_daily_special ? (
        <HiTag className="w-5 h-5 flex-shrink-0 animate-bounce" />
      ) : promo.free_item ? (
        <HiGift className="w-5 h-5 flex-shrink-0 animate-bounce" />
      ) : (
        <HiSpeakerphone className="w-5 h-5 flex-shrink-0 animate-bounce" />
      )}

      <p className="text-sm md:text-base font-medium text-center pr-8">
        {promo.is_daily_special && (
          <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs mr-2">
            TODAY ONLY
          </span>
        )}

        <span className="font-bold">{promo.title}</span>

        {discountText && (
          <span className="ml-2 bg-white/20 px-2 py-0.5 rounded-full text-sm">
            {discountText}
          </span>
        )}

        {promo.free_item && (
          <span className="ml-2">🎁 Free {promo.free_item}</span>
        )}

        {promo.promo_code && (
          <span className="ml-2 bg-white/30 px-2 py-0.5 rounded font-mono text-sm">
            Use code: {promo.promo_code}
          </span>
        )}

        {/* Short blurbs only — long birthday / Thanksgiving copy lives on the section cards. */}
        {promo.description && promo.description.length < 60 && (
          <span className="ml-2 opacity-90">— {promo.description}</span>
        )}
      </p>
    </>
  );

  const rowClass = 'container mx-auto flex items-center justify-center gap-3 flex-wrap';
  // Rose for informational promos (e.g. birthday); amber for discount / order-window style.
  const barClass =
    promo.discount_type === 'info'
      ? 'bg-gradient-to-r from-rose-500 to-pink-500 text-white py-3 px-4'
      : 'bg-gradient-to-r from-amber-500 to-orange-500 text-white py-3 px-4';

  return (
    <div className="relative">
      {promo.link_path ? (
        <Link to={promo.link_path} className={`block ${barClass} hover:opacity-90`}>
          <div className={rowClass}>{message}</div>
        </Link>
      ) : (
        <div className={barClass}>
          <div className={rowClass}>{message}</div>
        </div>
      )}

      <button
        onClick={() => onDismiss(promo.id)}
        className="absolute right-4 top-1/2 -translate-y-1/2 p-1 hover:bg-white/20 rounded-full transition-colors"
        aria-label={`Dismiss banner: ${promo.title}`}
      >
        <HiX className="w-5 h-5" />
      </button>
    </div>
  );
}

export default function PromoBanner() {
  const [promos, setPromos] = useState<Promo[]>([]);
  const [dismissedIds, setDismissedIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const stored = JSON.parse(sessionStorage.getItem('dismissed_promos') || '[]');
    setDismissedIds(stored);

    fetch('/api/promos/active?display_type=banner')
      .then((res) => res.json())
      .then((data) => {
        // Keep API priority order so Thanksgiving (priority 1) sits above birthday (2).
        setPromos(data.promos || []);
      })
      .catch((err) => {
        console.error('Failed to fetch promo banner:', err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const handleDismiss = (id: string) => {
    const next = [...dismissedIds, id];
    sessionStorage.setItem('dismissed_promos', JSON.stringify(next));
    setDismissedIds(next);
  };

  const visible = promos.filter((p) => !dismissedIds.includes(p.id));

  if (loading || visible.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col">
      {visible.map((promo) => (
        <BannerRow key={promo.id} promo={promo} onDismiss={handleDismiss} />
      ))}
    </div>
  );
}
